// socket.io-client wrapper with server-authoritative identity (the JWT is
// passed as `auth` at handshake; the server never trusts a payload identity)
// + reconnect with exponential backoff + state resync on (re)connect.

import { io, type Socket } from "socket.io-client";
import { useAuthStore } from "../store/authStore";
import { useGameStore } from "../store/gameStore";
import { api, ensureFreshToken } from "./api";
import type { DomainEventLike } from "./events";

const BACKEND_URL = ""; // same-origin

let socket: Socket | null = null;
// Lobby this client last joined on the current socket, remembered so the
// connect handler can re-attach after an auto-reconnect: a reconnect creates a
// brand-new server session with lobby_id=None, and without the re-join every
// action would be silently dropped (B5 fix).
let attachedLobbyId: string | null = null;

export function getSocket(): Socket | null {
  return socket;
}

export function connect(): Socket {
  const token = useAuthStore.getState().accessToken;
  if (!token) throw new Error("No access token; cannot connect WS");
  if (socket) socket.disconnect();
  attachedLobbyId = null; // fresh socket => fresh server session, nothing attached

  socket = io(BACKEND_URL, {
    // Dynamic auth: evaluated on EVERY (re)connect attempt, so a token
    // refreshed in the meantime is picked up without rebuilding the socket.
    auth: (cb) => cb({ token: useAuthStore.getState().accessToken ?? "" }),
    transports: ["websocket"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 15000,
  });

  socket.on("connect", () => {
    useGameStore.getState().setConnection("online");
    // B5: a (re)connect creates a brand-new server session with no lobby, so
    // re-attach by re-emitting join (idempotent server-side) + resync —
    // otherwise pushes stop and every action is silently dropped.
    if (attachedLobbyId) {
      wsActions.join(attachedLobbyId, useAuthStore.getState().username ?? "");
      void resync(attachedLobbyId);
    }
  });
  socket.on("disconnect", (reason: string) => {
    useGameStore.getState().setConnection(reason === "io client close" ? "offline" : "reconnecting");
  });
  socket.on("connect_error", async () => {
    useGameStore.getState().setConnection("reconnecting");
    // The handshake was refused — most likely because the access token
    // (15 min TTL) expired. Try a transparent refresh once; the dynamic
    // auth callback picks up the new token on the next retry. If the
    // refresh is rejected too, the session is dead: surface it and stop
    // the endless reconnect loop.
    const { accessToken, refreshToken, expiresAt } = useAuthStore.getState();
    if (!accessToken) return; // logged out — nothing to recover
    const expired = !expiresAt || Date.now() >= expiresAt - 5_000;
    if (expired && refreshToken) {
      const ok = await ensureFreshToken();
      if (!ok) {
        useGameStore.getState().setError("Sessione scaduta — effettua di nuovo il login.");
        if (socket?.io) socket.io.opts.reconnection = false;
        socket?.disconnect();
      }
    }
  });
  socket.on("reconnect", () => {
    useGameStore.getState().setConnection("online");
    const lobbyId = useGameStore.getState().lobbyId;
    if (lobbyId) void resync(lobbyId);
  });

  // Server-authoritative state push.
  socket.on("state_update", (view: unknown) => {
    useGameStore.getState().setGameState(view as never);
  });
  socket.on("events", (events: DomainEventLike[]) => {
    useGameStore.getState().ingestEvents(events);
  });
  socket.on("kicked", (data: { reason?: string }) => {
    attachedLobbyId = null; // kicked out: never re-attach to that lobby
    useGameStore.getState().markKicked(data?.reason ?? "Espulso dalla lobby");
  });
  // Server-side domain errors (rejected actions): surface them instead of
  // letting the action fail silently.
  socket.on("error", (err: { code?: string; message?: string; event?: string }) => {
    console.warn("[ws error]", err);
    useGameStore.getState().setError(err?.message ?? err?.code ?? "Errore");
  });

  return socket;
}

export function disconnect(): void {
  socket?.disconnect();
  socket = null;
  attachedLobbyId = null;
  useGameStore.getState().setConnection("offline");
}

// Resync: ask the server for the full state (also happens automatically on
// reconnect). Belt-and-braces — the WS state_update IS the source of truth.
export async function resync(lobbyId: string): Promise<void> {
  try {
    const state = await api.getState(lobbyId);
    useGameStore.getState().setGameState(state);
  } catch {
    // best-effort
  }
}

// Action emitters — identity comes from the session (server side); payload
// carries only the action data, never the userId.
export const wsActions = {
  join: (lobbyId: string, name: string) => {
    attachedLobbyId = lobbyId; // remembered for re-attach after an auto-reconnect (B5)
    socket?.emit("join", { lobby_id: lobbyId, name });
  },
  leave: (lobbyId: string) => {
    attachedLobbyId = null;
    socket?.emit("leave", { lobby_id: lobbyId });
  },
  rename: (name: string) => socket?.emit("rename", { name }),
  kick: (targetUserId: string) => socket?.emit("kick", { target_user_id: targetUserId }),
  start: () => socket?.emit("start", {}),
  declare: (tricks: number) => socket?.emit("declare", { tricks }),
  declareOne: (declaration: "win" | "lose") => socket?.emit("declare_one", { declaration }),
  play: (cardId: string, aceAsHigh: boolean, clientRequestId: string) =>
    socket?.emit("play", { card_id: cardId, ace_as_high: aceAsHigh, client_request_id: clientRequestId }),
  nextRound: () => socket?.emit("next_round", {}),
  reset: () => socket?.emit("reset", {}),
  sync: () => socket?.emit("sync", {}),
  heartbeat: () => socket?.emit("heartbeat", {}),
};